import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { Store } from '@ngxs/store';
import idx from 'idx';
import { slideRevealAnimation } from '../../../shared/animations/_index';
import { EditerFichePropriete } from '../../propriete.state';
import { ProprieteHelper } from '../../modeles/ProprieteHelper';
import { GtmService } from '../../../shared/services/gtm.service';
import { GTM_PUSH_CATEGORY_INTERRACTIONS_PAGES, GTM_PUSH_CATEGORY } from '../../../shared/modeles/variables';
import { OpenModalService } from '../../../shared/services/open-modal/open-modal.service';
import { DialogSupprimerProprieteComponent } from '../../../shared/components/dialog-supprimer-propriete/dialog-supprimer-propriete.component';
import { ProprieteModele } from '../../modeles/proprieteModel';
import { NgxTippyProps, NgxTippyService } from 'ngx-tippy-wrapper';
import { DownloadPhotoService } from '../../../propriete/services/download-photo-service/download-photo.service';
import { SafeUrl } from '@angular/platform-browser';

@Component({
  selector: 'app-carte-propriete-liste',
  templateUrl: './carte-propriete-liste.component.html',
  styleUrls: ['./carte-propriete-liste.component.scss'],
  animations: [slideRevealAnimation],
  standalone: false
})
export class CarteProprieteListeComponent implements OnInit {
  @Input()
  propriete: ProprieteModele;

  @Input()
  capaciteStatus: any;

  @Output()
  goToDetail = new EventEmitter();

  proprieteHelper: ProprieteHelper;

  readonly tippyProps: NgxTippyProps = {
    arrow: true,
    theme: 'light-border',
    allowHTML: true,
    interactive: true,
    animation: 'shift-toward',
    placement: 'bottom-end',
    trigger: 'click',
    offset: [10, 10],
    zIndex: 500,
    popperOptions: {
      // Empêche IE de throw une erreur d'affichage
      modifiers: [
        {
          name: 'flip',
          enabled: false
        },
        {
          name: 'preventOverflow',
          enabled: false
        }
      ]
    }
  };

  imageDownloadUrl: SafeUrl;

  constructor(
    private readonly _store: Store,
    private readonly _router: Router,
    private readonly _gtm: GtmService,
    private readonly _openModalService: OpenModalService,
    private readonly _tippyService: NgxTippyService,
    private readonly _downloadPhotoService: DownloadPhotoService
  ) {}

  ngOnInit() {
    this.proprieteHelper = new ProprieteHelper(this.propriete);
    if (this.propriete.photoId) {
      this.getPhotoUrl(this.propriete.photoId);
    }
  }

  getPhotoUrl(idPhoto: string): void {
    this._downloadPhotoService.getPhotoUrl(idPhoto).subscribe((photoUrl: SafeUrl) => {
      this.imageDownloadUrl = photoUrl;
    });
  }

  editProperty() {
    this._store.dispatch(new EditerFichePropriete(this.propriete.id, this._router.url));
  }

  deleteProperty() {
    const dialogRef = this._openModalService.openModal(DialogSupprimerProprieteComponent, { id: this.propriete.id });
    dialogRef.afterClosed().subscribe(() => document.getElementById('btn-carte-propriete-plus').focus());
  }

  hidePopover() {
    this._tippyService.hideAll();
  }

  get afficherAnalyseEcart() {
    return (
      this.propriete &&
      this.proprieteHelper.indicateur &&
      this.capaciteStatus.isCompleted &&
      !this.capaciteStatus.isTooLow &&
      !this.capaciteStatus.isTooHigh
    );
  }

  get isCapaciteManquante() {
    return !this.capaciteStatus.isCompleted;
  }

  get isBudgetImportant() {
    return this.capaciteStatus && this.capaciteStatus.isCompleted && this.capaciteStatus.isTooHigh;
  }

  get isBudgetInsuffisant() {
    return this.capaciteStatus && this.capaciteStatus.isCompleted && this.capaciteStatus.isTooLow;
  }

  get isMiseDeFondsInsuffisante() {
    return (
      !this.isBudgetImportant &&
      !this.isBudgetInsuffisant &&
      !this.isCapaciteManquante &&
      this.proprieteHelper.miseDeFondTropBasse
    );
  }

  get indicateur() {
    return this.proprieteHelper.indicateur;
  }

  get couleurIndicateur() {
    if (this.isBudgetImportant) {
      return 'vert';
    }

    if (this.isBudgetInsuffisant || this.isMiseDeFondsInsuffisante || this.isCapaciteManquante) {
      return 'blanc';
    }

    const mapping = {
      haut: 'rouge',
      moyen: 'jaune',
      bas: 'vert'
    };

    return this.indicateur && mapping[this.indicateur] ? mapping[this.indicateur] : null;
  }

  get texteIndicateur() {
    if (this.isBudgetImportant) {
      return 'propriete.detail.thermometre.titre-budget-important';
    }

    if (this.isBudgetInsuffisant) {
      return 'propriete.detail.thermometre.titre-budget-insuffisant';
    }

    if (this.isMiseDeFondsInsuffisante) {
      return 'propriete.detail.thermometre.titre-mise-fonds-basse';
    }

    const mapping = {
      haut: 'propriete.detail.thermometre.titre-non-recommande',
      moyen: 'propriete.detail.thermometre.titre-risque',
      bas: 'propriete.detail.thermometre.titre-realiste'
    };

    return this.indicateur && mapping[this.indicateur] ? mapping[this.indicateur] : null;
  }

  get hasThermometre() {
    return (
      !this.isMiseDeFondsInsuffisante &&
      !this.isCapaciteManquante &&
      !this.isBudgetInsuffisant &&
      !this.isBudgetImportant
    );
  }

  get bannerClass() {
    return 'indicateur-' + this.couleurIndicateur;
  }

  get montantVersementMensuel() {
    return idx(this.propriete, _ => _.analyseEcartPrequalification.montantVersementMensuel);
  }

  get montantFraisMensuels() {
    return this.proprieteHelper.montantFraisMensuels;
  }

  get montantTotalMensuel() {
    return this.montantVersementMensuel + this.montantFraisMensuels;
  }

  calculateBudget(event: Event) {
    event.stopPropagation();
    this._gtm.pushEvent({
      event: GTM_PUSH_CATEGORY_INTERRACTIONS_PAGES,
      category: GTM_PUSH_CATEGORY,
      label: 'Creation propriete',
      action: 'calculer capacite financiere',
      value: 0,
      nonInteraction: false
    });
    this._router.navigate(['capacite']);
  }
}
